import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const BASE_ROLES = [
  ["super_admin", "Super admin"],
  ["staff", "Staff"],
  ["partner", "Partner"],
  ["customer", "Customer"],
];

/** The audience list behind the "Who is this app for?" toggles on every app.
 *  Editable so a new kind of partner doesn't need a code change. */
export default function AppAudiencesCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ label: "", base_roles: ["partner"], partner_types: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/app-audiences");
      setRows(data.audiences || []);
    } catch {
      toast.error("Couldn't load the audience list");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (id, body) => {
    setBusy(id);
    try {
      const { data } = await api.patch(`/app-audiences/${id}`, body);
      toast.success(data.apps_resynced
        ? `Saved — ${data.apps_resynced} app${data.apps_resynced === 1 ? "" : "s"} updated`
        : "Saved");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save that");
      await load();
    } finally { setBusy(""); }
  };

  const remove = async (row) => {
    if (row.app_count && !window.confirm(
      `${row.label} is used by ${row.app_count} app(s). Deleting it removes that audience from them. Continue?`)) return;
    setBusy(row.audience_id);
    try {
      const { data } = await api.delete(`/app-audiences/${row.audience_id}`);
      toast.success(`${row.label} deleted — ${data.apps_updated} app(s) updated`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete that");
    } finally { setBusy(""); }
  };

  const create = async () => {
    setBusy("new");
    try {
      await api.post("/app-audiences", {
        label: draft.label,
        base_roles: draft.base_roles,
        partner_types: draft.partner_types.split(",").map((t) => t.trim()).filter(Boolean),
        position: (rows[rows.length - 1]?.position || 60) + 10,
      });
      toast.success("Audience added");
      setDraft({ label: "", base_roles: ["partner"], partner_types: "" });
      setAdding(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add that");
    } finally { setBusy(""); }
  };

  const toggleDraftRole = (r) => setDraft((d) => ({
    ...d,
    base_roles: d.base_roles.includes(r) ? d.base_roles.filter((x) => x !== r) : [...d.base_roles, r],
  }));

  return (
    <Card className="px-5 py-4" data-testid="app-audiences-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100">
            <Users className="h-4 w-4 text-slate-400" /> App audiences
          </h3>
          <p className="mt-1 max-w-2xl text-xs text-slate-500 dark:text-slate-400">
            These are the “Who is this app for?” toggles at the top of every app’s settings.
            An audience is a base role, optionally narrowed to particular partner types.
            Editing one updates every app that uses it.
          </p>
        </div>
        <Button size="sm" onClick={() => setAdding(!adding)} data-testid="app-audiences-add-toggle">
          <Plus className="mr-1.5 h-3.5 w-3.5" /> New audience
        </Button>
      </div>

      {adding && (
        <div className="mt-3 space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="app-audiences-new">
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input value={draft.label} data-testid="app-audiences-new-label"
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="e.g. Wholesale Partners" className="h-8 text-xs" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Partner types (comma separated, optional)</Label>
              <Input value={draft.partner_types} data-testid="app-audiences-new-types"
                onChange={(e) => setDraft({ ...draft, partner_types: e.target.value })}
                placeholder="wholesale, carrier" className="h-8 text-xs" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-500 dark:text-slate-400">Base role:</span>
            {BASE_ROLES.map(([id, lbl]) => (
              <button key={id} type="button" data-testid={`app-audiences-new-role-${id}`}
                onClick={() => toggleDraftRole(id)}
                className={`rounded-full border px-2 py-0.5 text-[11px] ${draft.base_roles.includes(id)
                  ? "border-primary bg-primary/10 font-semibold text-primary"
                  : "border-slate-200 text-slate-500 dark:border-slate-700 dark:text-slate-400"}`}>
                {lbl}
              </button>
            ))}
            <Button size="sm" className="ml-auto h-7 text-xs" onClick={create}
              disabled={busy === "new" || draft.label.trim().length < 2 || !draft.base_roles.length}
              data-testid="app-audiences-new-save">
              {busy === "new" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Add it
            </Button>
          </div>
        </div>
      )}

      <div className="mt-3 space-y-1.5">
        {loading ? (
          <div className="flex h-20 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" />
          </div>
        ) : rows.map((r) => (
          <div key={r.audience_id} data-testid={`app-audience-row-${r.audience_id}`}
            className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2">
            <Input defaultValue={r.label} data-testid={`app-audience-label-${r.audience_id}`}
              onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== r.label) patch(r.audience_id, { label: v }); }}
              className="h-7 max-w-[220px] text-xs font-medium" />
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              {(r.base_roles || []).join(", ")}
              {r.partner_types?.length ? ` · ${r.partner_types.join(", ")}` : ""}
            </span>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              · {r.app_count} app{r.app_count === 1 ? "" : "s"}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[11px] text-slate-500 dark:text-slate-400">In use</span>
              <Switch checked={r.active !== false} disabled={busy === r.audience_id}
                data-testid={`app-audience-active-${r.audience_id}`}
                onCheckedChange={(v) => patch(r.audience_id, { active: v })} />
              {r.audience_id !== "super" && (
                <button type="button" onClick={() => remove(r)} disabled={busy === r.audience_id}
                  data-testid={`app-audience-delete-${r.audience_id}`}
                  className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {r.description && (
              <p className="w-full text-[11px] text-slate-400 dark:text-slate-500">{r.description}</p>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
